import { chain, thirdWebClient } from "@/app/libs/thirdweb";
import { SPON, stSPON, ThawingPool } from "@/contracts/addresses";
import {
  getContract,
  prepareContractCall,
  readContract,
  sendAndConfirmTransaction,
} from "thirdweb";
import { Account } from "thirdweb/wallets";
import { isStakingDeprecated, STAKING_PAUSED_MESSAGE } from "@/config";

/**
 * Deposit tokens into the ThawingPool
 * @param {string} amount - The amount of tokens to deposit (in wei)
 * @param {string} to - The address to receive the stSPON tokens
 * @param {string} account - The thirdweb active account
 * @returns {Promise<hash>} - Transaction hash
 */
export async function depositToPool(
  amount: bigint,
  to: string,
  account: Account
) {
  // Staking is deprecated: never build a deposit transaction.
  if (isStakingDeprecated) {
    throw new Error(STAKING_PAUSED_MESSAGE);
  }
  const contractAddress = ThawingPool;
  const tokenAddress = SPON;
  try {
    const tokenContract = getContract({
      client: thirdWebClient,
      chain,
      address: tokenAddress,
    });

    const poolContract = getContract({
      client: thirdWebClient,
      chain,
      address: contractAddress,
    });

    const approveRequest = await prepareContractCall({
      contract: tokenContract,
      method: {
        inputs: [
          {
            internalType: "address",
            name: "spender",
            type: "address",
          },
          {
            internalType: "uint256",
            name: "value",
            type: "uint256",
          },
        ],
        name: "approve",
        outputs: [
          {
            internalType: "bool",
            name: "",
            type: "bool",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
      params: [contractAddress, amount],
    });

    const approveTx = await sendAndConfirmTransaction({
      transaction: approveRequest,
      account,
    });
    console.log(`Token approval successful: ${approveTx.transactionHash}`);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const depositRequest = await prepareContractCall({
      contract: poolContract,
      method: {
        type: "function",
        name: "deposit",
        inputs: [
          { name: "_amount", type: "uint256", internalType: "uint256" },
          { name: "_to", type: "address", internalType: "address" },
        ],
        outputs: [],
        stateMutability: "nonpayable",
      },
      params: [amount, to],
    });

    const depositTx = await sendAndConfirmTransaction({
      transaction: depositRequest,
      account,
    });
    console.log(`Deposit successful: ${depositTx.transactionHash}`);
    return depositTx.transactionHash;
  } catch (error) {
    console.error("Error depositing to pool:", error);
    throw error;
  }
}

/**
 * Request a withdrawal from the ThawingPool
 * @param {string} amount - The amount of tokens to withdraw (in wei)
 * @param {string} to - The address to receive the tokens
 * @param {string} account - The thirdweb active account
 * @returns {Promise<hash>} - Transaction hash
 */
export async function requestWithdrawal(
  amount: bigint,
  to: string,
  account: Account
) {
  const contractAddress = ThawingPool;
  const stSPONTokenAddress = stSPON;
  try {
    const stSPONTokenContract = getContract({
      client: thirdWebClient,
      chain,
      address: stSPONTokenAddress,
    });

    const poolContract = getContract({
      client: thirdWebClient,
      chain,
      address: contractAddress,
    });

    const approveRequest = await prepareContractCall({
      contract: stSPONTokenContract,
      method: {
        type: "function",
        name: "approve",
        inputs: [
          { name: "_spender", type: "address", internalType: "address" },
          { name: "_amount", type: "uint256", internalType: "uint256" },
        ],
        outputs: [{ name: "", type: "bool", internalType: "bool" }],
        stateMutability: "nonpayable",
      },
      params: [contractAddress, amount],
    });

    const approveTx = await sendAndConfirmTransaction({
      transaction: approveRequest,
      account,
    });
    console.log(`stToken approval successful: ${approveTx.transactionHash}`);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const withdrawRequest = await prepareContractCall({
      contract: poolContract,
      method: {
        inputs: [{ internalType: "uint256", name: "_amount", type: "uint256" }],
        name: "withdrawRequest",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
      },
      params: [amount],
    });

    const withdrawRequestTx = await sendAndConfirmTransaction({
      transaction: withdrawRequest,
      account,
    });
    console.log(
      `Withdrawal request successful: ${withdrawRequestTx.transactionHash}`
    );
    return withdrawRequestTx.transactionHash;
  } catch (error) {
    console.error("Error requesting withdrawal:", error);
    throw error;
  }
}

/**
 * Withdraw tokens from the ThawingPool after the vesting period
 * @param {string} account - The thirdweb active account
 * @returns {Promise<hash>} - Transaction hash
 */
export async function withdrawFromPool(account: Account) {
  const contractAddress = ThawingPool;
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: contractAddress,
    });

    const transaction = await prepareContractCall({
      contract,
      method: {
        type: "function",
        name: "withdraw",
        inputs: [],
        outputs: [],
        stateMutability: "nonpayable",
      },
      params: [],
    });

    const tx = await sendAndConfirmTransaction({
      transaction,
      account,
    });
    console.log(`Withdrawal successful: ${tx.transactionHash}`);
    return tx.transactionHash;
  } catch (error) {
    console.error("Error withdrawing from pool:", error);
    throw error;
  }
}

/**
 * Fetches the token balance of an account using readContract.
 *
 * @param account - The address to fetch the balance for.
 * @returns A promise that resolves to the balance in bigint.
 */

export async function getStSPONBalance(account: Account): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: stSPON,
    });

    const balance: bigint = await readContract({
      contract,
      method: {
        type: "function",
        name: "balanceOf",
        inputs: [
          { internalType: "address", name: "_account", type: "address" },
        ],
        outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
        stateMutability: "view",
      },
      params: [account.address],
    });

    console.log(`stSPON Balance for ${account.address}:`, balance);
    return balance;
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error;
  }
}

export async function getSPONBalance(account: Account): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: SPON,
    });

    const balance: bigint = await readContract({
      contract,
      method: {
        type: "function",
        name: "balanceOf",
        inputs: [
          { name: "_account", type: "address", internalType: "address" },
        ],
        outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
        stateMutability: "view",
      },
      params: [account.address],
    });

    console.log(`SPON Balance for ${account.address}:`, balance);
    return balance;
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error;
  }
}

export async function getTotalStakedSPON(): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: stSPON,
    });

    const stakedSPON: bigint = await readContract({
      contract,
      method: {
        type: "function",
        name: "totalSupply",
        inputs: [],
        outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
        stateMutability: "view",
      },
      params: [],
    });

    console.log(`Total Staked SPON:`, stakedSPON);
    return stakedSPON;
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error;
  }
}

export async function getWithdrawRequestDetails(account: Account) {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: ThawingPool,
    });

    const withdrawRequest = await readContract({
      contract,
      method: {
        type: "function",
        name: "withdrawRequests",
        inputs: [{ name: "", type: "address", internalType: "address" }],
        outputs: [
          { name: "shares", type: "uint256", internalType: "uint256" },
          { name: "unlockTime", type: "uint256", internalType: "uint256" },
        ],
        stateMutability: "view",
      },
      params: [account.address],
    });

    return withdrawRequest;
  } catch (error) {
    console.error("Error fetching withdraw request:", error);
    throw error;
  }
}

export async function fetchConvertToAssets(shares: bigint): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: stSPON,
    });

    const assets: bigint = await readContract({
      contract,
      method: {
        type: "function",
        name: "convertToAssets",
        inputs: [
          { name: "_sharesAmount", type: "uint256", internalType: "uint256" },
        ],
        outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
        stateMutability: "view",
      },
      params: [shares],
    });

    console.log(`Converted Assets:`, assets);
    return assets;
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error;
  }
}
